import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip, Badge, useTheme } from "@mui/material";
import { useSearchParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import CategoryIcon from "@mui/icons-material/Category";
import SettingsIcon from "@mui/icons-material/Settings";
import AnalyticsIcon from "@mui/icons-material/Analytics";

// New list-wise enterprise table components:
import PublicPrayersList from "./PublicPrayersList";
import PrivatePrayersList from "./PrivatePrayersList";
import PrayerCategories from "./PrayerCategory";
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import ModuleAnalyticsDashboard from "../components/ModuleAnalyticsDashboard";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ApprovalCenter from "./ApprovalCenter";

const TABS = [
  { value: "public", label: "Public Prayers", icon: PublicIcon },
  { value: "private", label: "Private Prayers", icon: LockIcon },
  { value: "categories", label: "Prayer Categories", icon: CategoryIcon },
];

const PrayersHub = ({ initialTab: propInitialTab }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [approvalPendingCount, setApprovalPendingCount] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';
        const cleanBaseUrl = rawApiUrl.replace(/\/+$/, '') + '/';
        const res = await fetch(`${cleanBaseUrl}approval-counts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          if (d.success) {
            setApprovalPendingCount(d.counts?.prayers ?? 0);
          }
        }
      } catch (e) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["public", "private", "categories", "approvals", "analytics"].includes(tabParam)) return tabParam;
    if (propInitialTab) return propInitialTab;
    if (location.pathname.includes("private-prayers")) return "private";
    if (location.pathname.includes("prayer-categories")) return "categories";
    if (location.pathname.includes("approval-center")) return "approvals";
    return "public";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam !== activeTab && ["public", "private", "categories", "approvals", "analytics"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const getModuleConfig = () => {
    if (activeTab === "private") {
      return { key: "private_prayer", title: "Private Prayers" };
    }
    if (activeTab === "categories") {
      return { key: "prayer_category", title: "Prayer Categories" };
    }
    if (activeTab === "approvals") {
      return { key: "approval_center", title: "Approval Center" };
    }
    if (activeTab === "analytics") {
      return { key: "prayer", title: "Prayer Analytics" };
    }
    return { key: "prayer", title: "Public Prayers" };
  };

  const BORDER_COLOR = isLight ? "#CBD5E1" : "rgba(76, 161, 175, 0.45)";
  const PANEL_BG = isLight ? "#FFFFFF" : "#182232";

  return (
    <Box sx={{ width: "100%", pb: 4, pt: 1 }}>
      <Helmet><title>Prayers - GPN</title></Helmet>

      {/* ── Top Bar: Module Title + Tabs on left, Settings icon at far right ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 2,
          mb: "-1.5px",
          px: 1,
        }}
      >
        {/* LEFT: Module Title + Tabs grouped together */}
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          {/* Module Title — styled as a label, NOT a button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              pb: 1,
              px: 1,
              mr: 3.5,
              userSelect: "none",
              cursor: "default",
              borderBottom: isLight ? "2.5px solid #0D9488" : "2.5px solid rgba(76, 161, 175, 0.65)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: isLight ? "rgba(13, 148, 136, 0.12)" : "rgba(76, 161, 175, 0.18)",
                border: isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1.5px solid rgba(76, 161, 175, 0.5)",
                color: isLight ? "#0D9488" : "#64d2e2",
                flexShrink: 0,
              }}
            >
              <Diversity3Icon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{
                color: isLight ? "#0F172A" : "#e0f2fe",
                letterSpacing: "0.6px",
                fontSize: "1.25rem",
                lineHeight: 1.15,
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Prayers
            </Typography>
          </Box>

          {/* Vertical Divider separating Title from Tab buttons */}
          <Box
            sx={{
              width: "1.5px",
              height: 24,
              bgcolor: isLight ? "#CBD5E1" : "rgba(255, 255, 255, 0.15)",
              mr: 2.5,
              mb: 0.8,
            }}
          />

          {/* Tabs */}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
            {TABS.map(({ value, label, icon: Icon, hasBadge }) => {
              const isActive = activeTab === value;
              return (
                <Box
                  key={value}
                  onClick={() => handleTabChange(value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    px: 2.4,
                    py: 1.1,
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: "0.88rem",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? (isLight ? "#0D9488" : "#FFFFFF") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)"),
                    transition: "all 0.2s ease",
                    ...(isActive
                      ? {
                          backgroundColor: PANEL_BG,
                          borderTop: `1.5px solid ${BORDER_COLOR}`,
                          borderLeft: `1.5px solid ${BORDER_COLOR}`,
                          borderRight: `1.5px solid ${BORDER_COLOR}`,
                          borderRadius: "8px 8px 0 0",
                          position: "relative",
                          zIndex: 10,
                          boxShadow: isLight ? "0 -2px 6px rgba(15, 23, 42, 0.05)" : "none",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: "-2px",
                            height: "4px",
                            backgroundColor: PANEL_BG,
                            zIndex: 12,
                          },
                        }
                      : {
                          backgroundColor: "transparent",
                          border: "none",
                          "&:hover": {
                            color: isLight ? "#0D9488" : "#FFFFFF",
                            backgroundColor: isLight ? "#F1F5F9" : "rgba(255,255,255,0.04)",
                            borderRadius: "8px 8px 0 0",
                          },
                        }),
                  }}
                >
                  <Icon sx={{ fontSize: 17, color: isActive ? (isLight ? "#0D9488" : "#4CA1AF") : (isLight ? "#64748B" : "rgba(255, 255, 255, 0.55)") }} />
                  <span>{label}</span>
                  {hasBadge && approvalPendingCount > 0 && (
                    <Box
                      sx={{
                        ml: 0.5,
                        bgcolor: '#DC2626',
                        color: '#FFFFFF',
                        borderRadius: '10px',
                        px: 0.8,
                        py: 0.15,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 18,
                        height: 18,
                        animation: 'blinkPendingBadge 1.8s ease-in-out infinite',
                        boxShadow: '0 0 8px rgba(220, 38, 38, 0.6)',
                        '@keyframes blinkPendingBadge': {
                          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                          '50%': { transform: 'scale(1.15)', opacity: 0.7, boxShadow: '0 0 14px rgba(220, 38, 38, 1)' }
                        }
                      }}
                    >
                      {approvalPendingCount}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT: Action Icons Group (Approval Center, Prayer Analytics, Settings) positioned together */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "6px", mr: 0.5, zIndex: 10 }}>
          {/* Approval Center Icon Button */}
          <Tooltip title={`Approval Center${approvalPendingCount > 0 ? ` (${approvalPendingCount} Pending)` : ''}`} placement="bottom">
            <IconButton
              onClick={() => handleTabChange("approvals")}
              sx={{
                color: activeTab === "approvals" ? "#FFFFFF" : (isLight ? "#0D9488" : "#64d2e2"),
                bgcolor: activeTab === "approvals"
                  ? (isLight ? "#0D9488" : "#177373")
                  : (isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)"),
                border: activeTab === "approvals"
                  ? "1.5px solid #64d2e2"
                  : (isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)"),
                borderRadius: "8px",
                p: 0.85,
                position: "relative",
                transition: "all 0.2s ease-in-out",
                boxShadow: activeTab === "approvals" ? "0 0 12px rgba(100, 210, 226, 0.4)" : "none",
                "&:hover": {
                  bgcolor: activeTab === "approvals"
                    ? (isLight ? "#0f766e" : "#146262")
                    : (isLight ? "rgba(13, 148, 136, 0.2)" : "rgba(76, 161, 175, 0.3)"),
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Badge
                badgeContent={approvalPendingCount}
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: '#DC2626',
                    color: '#FFFFFF',
                    fontWeight: 800,
                    fontSize: '0.62rem',
                    height: 16,
                    minWidth: 16,
                    px: 0.4,
                    border: '1.5px solid #182232',
                    boxShadow: '0 0 6px rgba(220, 38, 38, 0.8)',
                    animation: approvalPendingCount > 0 ? 'pulseBadge 2s infinite ease-in-out' : 'none',
                    '@keyframes pulseBadge': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.15)' }
                    }
                  }
                }}
              >
                <PendingActionsIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Prayer Analytics Icon Button */}
          <Tooltip title="Prayer Analytics" placement="bottom">
            <IconButton
              onClick={() => handleTabChange("analytics")}
              sx={{
                color: activeTab === "analytics" ? "#FFFFFF" : (isLight ? "#0D9488" : "#64d2e2"),
                bgcolor: activeTab === "analytics"
                  ? (isLight ? "#0D9488" : "#177373")
                  : (isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)"),
                border: activeTab === "analytics"
                  ? "1.5px solid #64d2e2"
                  : (isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)"),
                borderRadius: "8px",
                p: 0.85,
                transition: "all 0.2s ease-in-out",
                boxShadow: activeTab === "analytics" ? "0 0 12px rgba(100, 210, 226, 0.4)" : "none",
                "&:hover": {
                  bgcolor: activeTab === "analytics"
                    ? (isLight ? "#0f766e" : "#146262")
                    : (isLight ? "rgba(13, 148, 136, 0.2)" : "rgba(76, 161, 175, 0.3)"),
                  transform: "translateY(-1px)",
                },
              }}
            >
              <AnalyticsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Settings Icon Button */}
          <Tooltip title={`${getModuleConfig().title} Settings`} placement="bottom">
            <IconButton
              onClick={() => setSettingsOpen(true)}
              sx={{
                color: isLight ? "#0D9488" : "#64d2e2",
                bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)",
                border: isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)",
                borderRadius: "8px",
                p: 0.85,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.18)" : "rgba(76, 161, 175, 0.3)",
                  borderColor: isLight ? "#0D9488" : "#4CA1AF",
                  transform: "rotate(30deg)",
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Main Content Box: Connected directly under the active tab ── */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          border: `1.5px solid ${BORDER_COLOR}`,
          borderRadius: "0 10px 10px 10px",
          backgroundColor: PANEL_BG,
          backdropFilter: "blur(20px)",
          boxShadow: isLight ? "0 4px 20px rgba(15, 23, 42, 0.08)" : "0 10px 30px rgba(0, 0, 0, 0.35)",
          p: { xs: 1.5, sm: 2, md: 2.8 },
          position: "relative",
          zIndex: 1,
          minHeight: "75vh",
        }}
      >
        {/* Previous grid/card layout (commented out as requested): */}
        {/* {activeTab === "public" && <PublicPrayers hideHeader={true} />} */}
        {/* {activeTab === "private" && <PrivatePrayers hideHeader={true} />} */}

        {/* Enterprise tab contents: */}
        {activeTab === "public" && <PublicPrayersList key="public" hideHeader={true} />}
        {activeTab === "private" && <PrivatePrayersList key="private" hideHeader={true} initialVisibility="all" />}
        {activeTab === "categories" && <PrayerCategories key="categories" hideHeader={true} />}
        {activeTab === "approvals" && <ApprovalCenter hideHeader={true} dedicatedModule="prayers" />}
        {activeTab === "analytics" && (
          <ModuleAnalyticsDashboard
            contentType="prayer"
            moduleTitle="Prayer Activity & Engagement Analytics"
            hideHeader={true}
          />
        )}
      </Box>

      <ModuleSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        moduleKey={getModuleConfig().key}
        moduleTitle={getModuleConfig().title}
      />
    </Box>
  );
};

export default PrayersHub;
